import { useEffect, useState, useRef, ChangeEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../supabaseClient";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Paperclip, Send, X, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface MentorAttachment {
  url: string;
  name: string;
  type?: string | null;
  size?: number | null;
}

interface MentorMessage {
  id: string;
  user_id: string;
  mentor_id: string;
  sender_type: string;
  message: string | null;
  attachments: MentorAttachment[] | null;
  created_at: string;
}

interface InboxUser {
  id: string;
  name: string;
  lastMessageAt: string;
  hasUnread: boolean;
  messageCount: number;
  unreadCount: number;
}

interface MentorInboxPageProps {
  mentorId: string;
}

export function MentorInboxPage({ mentorId }: MentorInboxPageProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [users, setUsers] = useState<InboxUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<InboxUser | null>(null);
  const [messages, setMessages] = useState<MentorMessage[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [input, setInput] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);

  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<MentorMessage | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingConversation, setIsDeletingConversation] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = (behavior: ScrollBehavior = "auto") => {
    const el = messagesEndRef.current;
    if (!el) return;

    // Wait for layout to settle (especially after switching users / loading).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior, block: "end" });
      });
    });
  };

  const totalUnread = useMemo(() => {
    return users.reduce((sum, u) => sum + (u.unreadCount || 0), 0);
  }, [users]);

  useEffect(() => {
    const loadUsers = async () => {
      setIsLoadingUsers(true);

      const { data, error } = await supabase
        .from("mentor_messages")
        .select("user_id, created_at, sender_type, is_read_by_mentor, message, attachments")
        .eq("mentor_id", mentorId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Failed to load mentor inbox users", error);
        setUsers([]);
        setIsLoadingUsers(false);
        return;
      }

      const seen = new Set<string>();
      const lastByUser: Record<string, string> = {};
      const hasUnreadByUser: Record<string, boolean> = {};
      const messageCountByUser: Record<string, number> = {};
      const unreadCountByUser: Record<string, number> = {};
      const orderedIds: string[] = [];

      for (const row of data || []) {
        const uid = row.user_id as string;
        if (!seen.has(uid)) {
          seen.add(uid);
          orderedIds.push(uid);
        }
        const createdAt = row.created_at as string;
        if (!lastByUser[uid] || createdAt > lastByUser[uid]) {
          lastByUser[uid] = createdAt;
        }

        const senderType = (row.sender_type as string) || "user";
        const isRead = (row.is_read_by_mentor as boolean | null) ?? false;

        // Credit/message counting:
        // - Treat any user-sent row with either text or attachments as a message.
        const text = (row.message as string | null) ?? "";
        const hasText = text.trim().length > 0;
        const attachments = (row as any).attachments as unknown;
        const hasAttachments = Array.isArray(attachments) && attachments.length > 0;
        const isCredit = senderType === "user" && (hasText || hasAttachments);

        if (isCredit) {
          messageCountByUser[uid] = (messageCountByUser[uid] || 0) + 1;

          if (!isRead) {
            unreadCountByUser[uid] = (unreadCountByUser[uid] || 0) + 1;
            hasUnreadByUser[uid] = true;
          }
        }
      }

      if (orderedIds.length === 0) {
        setUsers([]);
        setSelectedUser(null);
        setIsLoadingUsers(false);
        return;
      }

      const { data: profiles } = await supabase
        .from("user_profiles")
        .select("id, full_name")
        .in("id", orderedIds);

      const nameById: Record<string, string> = {};
      for (const row of profiles || []) {
        const fullName = (row.full_name as string | undefined)?.trim();
        nameById[row.id as string] =
          fullName && fullName.length > 0
            ? fullName
            : t("mentorInbox.unknownUser", { defaultValue: "User" });
      }

      const list: InboxUser[] = orderedIds.map((id) => ({
        id,
        name:
          nameById[id] ||
          t("mentorInbox.unknownUser", { defaultValue: "User" }),
        lastMessageAt: lastByUser[id],
        hasUnread: !!hasUnreadByUser[id],
        messageCount: messageCountByUser[id] || 0,
        unreadCount: unreadCountByUser[id] || 0,
      }));

      // Unread conversations always on top, sorted by latest message time
      list.sort((a, b) => {
        if (a.hasUnread !== b.hasUnread) {
          return a.hasUnread ? -1 : 1;
        }
        // Newest first
        return a.lastMessageAt < b.lastMessageAt ? 1 : -1;
      });

      setUsers(list);
      setSelectedUser(list[0] || null);
      setIsLoadingUsers(false);
    };

    loadUsers();
  }, [mentorId, t]);

  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedUser) {
        setMessages([]);
        return;
      }

      setIsLoadingMessages(true);

      const { data, error } = await supabase
        .from("mentor_messages")
        .select("id, user_id, mentor_id, sender_type, message, attachments, created_at")
        .eq("mentor_id", mentorId)
        .eq("user_id", selectedUser.id)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Failed to load mentor conversation", error);
        setMessages([]);
      } else {
        const mapped: MentorMessage[] = (data || []).map((row) => ({
          id: row.id,
          user_id: row.user_id,
          mentor_id: row.mentor_id,
          sender_type: row.sender_type,
          message: row.message ?? null,
          attachments: (row.attachments as MentorAttachment[] | null) || null,
          created_at: row.created_at,
        }));
        setMessages(mapped);
      }

      // Mark all user messages in this conversation as read (server-side to avoid RLS issues)
      try {
        await supabase.functions.invoke("mark-mentor-messages-read", {
          body: { userId: selectedUser.id },
        });

        // Clear unread badge for this user in local state
        setUsers((prev) =>
          prev.map((u) =>
            u.id === selectedUser.id
              ? { ...u, hasUnread: false, unreadCount: 0 }
              : u,
          ),
        );
      } catch (err) {
        console.error("Failed to mark mentor messages as read", err);
      }

      setIsLoadingMessages(false);
    };

    loadMessages();
  }, [mentorId, selectedUser]);

  useEffect(() => {
    // New messages: keep view pinned to bottom.
    scrollToBottom("smooth");
  }, [messages]);

  useEffect(() => {
    // When opening/switching a conversation, ensure we start at the bottom
    // (the initial render often happens while loading/with different heights).
    if (!selectedUser) return;
    if (isLoadingMessages) return;
    scrollToBottom("auto");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.id, isLoadingMessages]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const selectedFiles = Array.from(e.target.files);
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    const oversizedFiles = selectedFiles.filter((file) => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      toast({
        title: t("mentorChat.fileTooLargeTitle", {
          defaultValue: "File too large",
        }),
        description: t("mentorChat.fileTooLargeDescription", {
          defaultValue: "Each attachment must be 10MB or smaller.",
        }),
        variant: "destructive",
      });

      const validFiles = selectedFiles.filter((file) => file.size <= MAX_FILE_SIZE);

      if (validFiles.length === 0) {
        e.target.value = "";
        setFiles([]);
        return;
      }

      setFiles(validFiles);
      return;
    }

    setFiles(selectedFiles);
  };

  const openDeleteMessageDialog = (msg: MentorMessage) => {
    setMessageToDelete(msg);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteMessage = async () => {
    if (!messageToDelete) return;
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("mentor_messages")
        .delete()
        .eq("id", messageToDelete.id);

      if (error) {
        console.error("Failed to delete mentor message", error);
        toast({
          title: t("mentorInbox.deleteFailedTitle", { defaultValue: "Delete failed" }),
          description: t("mentorInbox.deleteFailedDesc", {
            defaultValue: "Could not delete this message. Please try again.",
          }),
          variant: "destructive",
        });
        return;
      }

      setMessages((prev) => prev.filter((m) => m.id !== messageToDelete.id));
      toast({
        title: t("mentorInbox.deleteSuccessTitle", { defaultValue: "Message deleted" }),
        description: t("mentorInbox.deleteSuccessDesc", {
          defaultValue: "This message has been permanently removed.",
        }),
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setMessageToDelete(null);
    }
  };

  const handleDeleteConversation = async () => {
    if (!selectedUser) return;
    setIsDeletingConversation(true);
    try {
      const { error } = await supabase
        .from("mentor_messages")
        .delete()
        .eq("mentor_id", mentorId)
        .eq("user_id", selectedUser.id);

      if (error) {
        console.error("Failed to delete mentor conversation", error);
        toast({
          title: t("mentorInbox.deleteConversationFailedTitle", { defaultValue: "Delete failed" }),
          description: t("mentorInbox.deleteConversationFailedDesc", {
            defaultValue: "Could not delete this conversation. Please try again.",
          }),
          variant: "destructive",
        });
        return;
      }

      setMessages([]);
      setUsers((prev) => prev.filter((u) => u.id !== selectedUser.id));
      setSelectedUser(null);
      toast({
        title: t("mentorInbox.deleteConversationSuccessTitle", { defaultValue: "Conversation deleted" }),
        description: t("mentorInbox.deleteConversationSuccessDesc", {
          defaultValue: "All messages in this conversation have been removed.",
        }),
      });
    } finally {
      setIsDeletingConversation(false);
    }
  };

  const handleSend = async () => {
    if (!selectedUser) return;
    if (!input.trim() && files.length === 0) return;

    setIsSending(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) return;

      const attachments: MentorAttachment[] = [];

      if (files.length > 0) {
        for (const [index, file] of files.entries()) {
          const extIndex = file.name.lastIndexOf(".");
          const ext = extIndex >= 0 ? file.name.slice(extIndex) : "";
          const safeFileName = `${Date.now()}_${index}_${Math.random().toString(36).slice(2, 8)}${ext}`;
          const path = `${selectedUser.id}/${mentorId}/${safeFileName}`;

          const { error: uploadError } = await supabase
            .storage
            .from("mentor-attachments")
            .upload(path, file);

          if (uploadError) {
            console.error("Upload error", uploadError);
            continue;
          }

          const { data: publicUrlData } = supabase
            .storage
            .from("mentor-attachments")
            .getPublicUrl(path);

          const publicUrl = publicUrlData.publicUrl;

          attachments.push({
            url: publicUrl,
            name: file.name,
            type: file.type || null,
            size: file.size,
          });
        }
      }

      const { data, error } = await supabase
        .from("mentor_messages")
        .insert({
          user_id: selectedUser.id,
          mentor_id: mentorId,
          sender_type: "mentor",
          message: input.trim() || null,
          attachments: attachments.length > 0 ? attachments : null,
          is_read_by_mentor: true,
        })
        .select()
        .single();

      if (error) {
        console.error("Failed to send mentor reply", error);
      } else if (data) {
        const newMessage: MentorMessage = {
          id: data.id,
          user_id: data.user_id,
          mentor_id: data.mentor_id,
          sender_type: data.sender_type,
          message: data.message ?? null,
          attachments: (data.attachments as MentorAttachment[] | null) || null,
          created_at: data.created_at,
        };

        setMessages((prev) => [...prev, newMessage]);
        setInput("");
        setFiles([]);

        // Fire-and-forget: notify the user that the mentor replied (Web Push)
        try {
          await supabase.functions.invoke("notify-user-message", {
            body: {
              userId: selectedUser.id,
              mentorId,
              messageId: data.id,
            },
          });
        } catch (notifyError) {
          console.error("Failed to trigger user notification", notifyError);
        }
      }
    } finally {
      setIsSending(false);
    }
  };

  const title = t("mentorInbox.title", {
    defaultValue: "Mentor Inbox",
  });

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4 flex flex-col gap-4 h-[calc(100vh-5rem)] relative">
      <div className="mb-8 text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">
          {title}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto text-sm">
          {t("mentorInbox.subtitle", {
            defaultValue:
              "Here you can see all users who have written to you. Select a user to view and reply to your shared conversation.",
          })}
        </p>
      </div>

      {/* Mobile: left edge handle + drawer */}
      <button
        type="button"
        onClick={() => setIsUserDrawerOpen(true)}
        className="md:hidden absolute left-0 top-1/2 -translate-y-1/2 h-20 w-5 rounded-r-xl bg-primary/20 border border-primary/30 backdrop-blur flex items-center justify-center"
        aria-label={t("mentorInbox.userListTitle", { defaultValue: "Users with conversations" })}
      >
        {totalUnread > 0 && (
          <span className="absolute -top-2 -right-2 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] leading-none min-w-5 h-5 px-1">
            {totalUnread}
          </span>
        )}
      </button>

      <Sheet open={isUserDrawerOpen} onOpenChange={setIsUserDrawerOpen}>
        <SheetContent side="left" className="p-0 w-[82vw] max-w-xs">
          <Card className="h-full rounded-none border-0 flex flex-col min-h-0">
            <CardHeader>
              <CardTitle className="text-base font-semibold">
                {t("mentorInbox.userListTitle", {
                  defaultValue: "Users with conversations",
                })}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 min-h-0">
              {isLoadingUsers ? (
                <p className="text-sm text-muted-foreground">
                  {t("common.loading", { defaultValue: "Loading users..." })}
                </p>
              ) : users.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("mentorInbox.empty", {
                    defaultValue:
                      "No users have started a conversation with you yet.",
                  })}
                </p>
              ) : (
                <div className="flex flex-col gap-2 overflow-y-auto pr-1 no-scrollbar h-full">
                  {users.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => {
                        setSelectedUser(u);
                        setMessages([]);
                        setIsUserDrawerOpen(false);
                      }}
                      className={`w-full px-3 py-2 rounded-lg border text-xs flex items-center justify-between gap-2 text-left transition ${
                        selectedUser && selectedUser.id === u.id
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-purple-50 text-purple-900 border-purple-100 hover:bg-purple-100"
                      }`}
                    >
                      <span className="truncate flex-1 min-w-0">{u.name}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="h-4 w-px bg-current/20" aria-hidden="true" />
                        <span className="text-[11px] opacity-80">{u.messageCount}</span>
                        {u.unreadCount > 0 && (
                          <span className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] leading-none min-w-4 h-4 px-1">
                            {u.unreadCount}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </SheetContent>
      </Sheet>

      <div className="flex flex-col md:flex-row gap-4 flex-1 min-h-0">
        <Card className="hidden md:flex w-full md:w-52 lg:w-60 flex-col min-h-0 overflow-hidden md:self-stretch md:flex-none">
          <CardHeader>
            <CardTitle className="text-base font-semibold">
              {t("mentorInbox.userListTitle", {
                defaultValue: "Users with conversations",
              })}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 min-h-0">
            {isLoadingUsers ? (
              <p className="text-sm text-muted-foreground">
                {t("common.loading", { defaultValue: "Loading users..." })}
              </p>
            ) : users.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("mentorInbox.empty", {
                  defaultValue:
                    "No users have started a conversation with you yet.",
                })}
              </p>
            ) : (
              <div className="flex flex-col gap-2 overflow-y-auto pr-1 no-scrollbar h-full">
                {users.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => {
                      setSelectedUser(u);
                      setMessages([]);
                    }}
                    className={`w-full px-3 py-2 rounded-lg border text-xs flex items-center justify-between gap-2 text-left transition ${
                      selectedUser && selectedUser.id === u.id
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-purple-50 text-purple-900 border-purple-100 hover:bg-purple-100"
                    }`}
                  >
                    <span className="truncate flex-1 min-w-0">{u.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="h-4 w-px bg-current/20" aria-hidden="true" />
                      <span className="text-[11px] opacity-80">
                        {u.messageCount}
                      </span>
                      {u.unreadCount > 0 && (
                        <span className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] leading-none min-w-4 h-4 px-1">
                          {u.unreadCount}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="flex-1 min-h-0 flex flex-col">
          <CardHeader className="py-3 border-b">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {selectedUser && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUser(null);
                      setMessages([]);
                    }}
                    aria-label={t("common.cancel", { defaultValue: "Close" })}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-transparent text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
                <CardTitle className="text-sm font-medium truncate">
                  {selectedUser
                    ? t("mentorInbox.conversationWith", {
                        name: selectedUser.name,
                        defaultValue: "Conversation with {{name}}",
                      })
                    : t("mentorInbox.guideTitle", {
                        defaultValue:
                          "Inner Gem Mentor Guide: Being a Mirror of Awakening",
                      })}
                </CardTitle>
              </div>
              {selectedUser && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteConversation}
                  disabled={isDeletingConversation}
                  className="ml-2"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  {t("mentorInbox.deleteConversation", {
                    defaultValue: "Delete conversation",
                  })}
                </Button>
              )}
            </div>
          </CardHeader>

          {selectedUser ? (
            <>
              <CardContent className="flex-1 min-h-0 pt-3 pb-2 px-2 overflow-y-auto space-y-3 no-scrollbar">
                {isLoadingMessages ? (
                  <p className="text-center text-sm text-muted-foreground">
                    {t("common.loading", {
                      defaultValue: "Loading conversation...",
                    })}
                  </p>
                ) : messages.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground">
                    {t("mentorInbox.noMessages", {
                      defaultValue:
                        "No messages in this conversation yet. You can send a first reply.",
                    })}
                  </p>
                ) : (
                  messages.map((msg) => {
                    const isMine = msg.sender_type === "mentor";

                    return (
                      <div
                        key={msg.id}
                        className={`group relative flex flex-col gap-1 ${
                          isMine ? "items-end" : "items-start"
                        }`}
                      >
                        {msg.message && (
                          <div
                            className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap shadow-sm ${
                              isMine
                                ? "bg-primary text-primary-foreground"
                                : "bg-primary/5 text-primary"
                            }`}
                          >
                            {msg.message}
                          </div>
                        )}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div
                            className={`max-w-[80%] flex flex-col gap-1 mt-1 ${
                              isMine ? "items-end" : "items-start"
                            }`}
                          >
                            {msg.attachments.map((file, index) => (
                              <a
                                key={`${msg.id}-att-${index}`}
                                href={file.url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 text-xs text-purple-900 bg-purple-50 border border-purple-100 rounded-xl px-3 py-2 hover:bg-purple-100 shadow-sm"
                              >
                                <Paperclip className="w-3 h-3" />
                                <span className="truncate max-w-[200px] font-medium">
                                  {file.name}
                                </span>
                              </a>
                            ))}
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() => openDeleteMessageDialog(msg)}
                          className={`absolute top-0 ${
                            isMine ? "left-0" : "right-0"
                          } -mt-2 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-muted-foreground flex items-center gap-1 bg-background/80 rounded-full px-2 py-0.5 shadow-sm border`}
                        >
                          <Trash2 className="w-3 h-3" />
                          {t("common.delete", { defaultValue: "Delete" })}
                        </button>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </CardContent>

              <div className="border-t px-4 py-3 space-y-2">
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
                    <Paperclip className="w-4 h-4" />
                    <span>
                      {t("mentorChat.uploadLabel", {
                        defaultValue: "Attach files (images, PDF, docs)",
                      })}
                    </span>
                    <Input
                      type="file"
                      multiple
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      onChange={handleFileChange}
                    />
                  </label>
                  {files.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {t("mentorChat.selectedFiles", {
                        count: files.length,
                        defaultValue: "{{count}} file(s) selected",
                      })}
                    </span>
                  )}
                </div>


                <div className="flex items-end gap-2">
                  <Textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={t("mentorInbox.inputPlaceholder", {
                      defaultValue: "Write a reply to this user...",
                    })}
                    className="min-h-[96px] max-h-[220px] resize-none overflow-y-auto no-scrollbar"
                    disabled={!selectedUser || isSending}
                  />
                  <Button
                    type="button"
                    onClick={handleSend}
                    disabled={
                      !selectedUser ||
                      isSending ||
                      (!input.trim() && files.length === 0)
                    }
                    className="shrink-0 h-10 w-10 rounded-full p-0 flex items-center justify-center"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <CardContent className="flex-1 min-h-0 pt-4 pb-4 px-4 overflow-y-auto no-scrollbar">
              <p className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground">
                {t("mentorInbox.guideBody", {
                  defaultValue:
                    "🌟 Inner Gem Mentor Guide: Being a Mirror of Awakening\n\nOur Core Intention: Inner Gem is dedicated to guiding users toward inner awareness. When responding, we encourage transcending conventional, worldly advice. Instead, focus on helping them see through illusions, guiding them to 'remember' that they are already inherently whole and complete, and to realize the truth that 'all is a manifestation of the mind.'\n\nService & Reply Guidelines:\n\nInquiries & Mechanism: Each payment unlocks the user's ability to submit a single deep dive inquiry (sending one core message).\n\nUnlimited Replies: As a mentor, your responses are not limited by a message count. You are welcome to send messages in segments—whether offering comforting words, in-depth written insights, or meditation suggestions—to create the most supportive healing experience.\n\n24-Hour Presence: To foster a safe and supportive environment, we recommend providing a thoughtful reply within 24 hours of receiving an inquiry. If a question requires more time for reflection, simply send a brief, comforting message to acknowledge the user and kindly let them know when to expect your full response.",
                })}
              </p>
            </CardContent>
          )}
        </Card>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("mentorInbox.deleteConfirmTitle", { defaultValue: "Delete this message?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("mentorInbox.deleteConfirmDesc", {
                defaultValue:
                  "This will permanently remove this message for both you and the user. This action cannot be undone.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteMessage}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.delete", { defaultValue: "Delete" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
